/**
 * Mobile Auth Bridge — `/auth/mobile-bridge`.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 *
 * Flow:
 *   1. Mobile app opens https://auth.emergentagent.com/?redirect=<backend>/auth/mobile-bridge
 *   2. After Google sign-in, Emergent appends `#session_id=XYZ` and lands here
 *   3. We deep-link `diplomexa://auth/callback?session_id=XYZ` back into the
 *      Diplomexa mobile app, which exchanges the id for a JWT via
 *      `POST /api/auth/google-emergent`
 *   4. If the deep-link doesn't fire (browser opened on a device without the
 *      app installed, or the user dismissed the system prompt) we show a
 *      friendly "Open Diplomexa app" fallback button.
 */
import { useEffect, useMemo, useState } from "react";

export default function MobileAuthBridge() {
  const [showFallback, setShowFallback] = useState(false);
  const [hasSession, setHasSession] = useState(true);

  const sessionId = useMemo(() => {
    if (typeof window === "undefined") return null;
    // Emergent puts session_id in the URL fragment after #
    const hash = (window.location.hash || "").replace(/^#/, "");
    const hashParams = new URLSearchParams(hash);
    const fromHash = hashParams.get("session_id");
    if (fromHash) return fromHash;
    // Also accept ?session_id=… just in case
    const queryParams = new URLSearchParams(window.location.search || "");
    return queryParams.get("session_id");
  }, []);

  const deepLink = sessionId
    ? `diplomexa://auth/callback?session_id=${encodeURIComponent(sessionId)}`
    : null;

  useEffect(() => {
    if (!sessionId) {
      setHasSession(false);
      setShowFallback(true);
      return;
    }
    // Fire the deep-link immediately. If the OS routes it, the app opens and
    // this tab can be closed by the user. If not, we surface the fallback CTA.
    try {
      window.location.replace(deepLink);
    } catch {
      /* ignore — fallback will be shown */
    }
    const t = setTimeout(() => setShowFallback(true), 1200);
    return () => clearTimeout(t);
  }, [sessionId, deepLink]);

  return (
    <div
      data-testid="mobile-auth-bridge"
      className="min-h-screen flex flex-col items-center justify-center text-white px-6 text-center"
      style={{ background: "radial-gradient(circle at 20% 0%, #14264F, #0A192F 60%, #050C1B)" }}
    >
      <div
        className="relative h-14 w-14 rounded-xl bg-[#0A192F] border border-[#D4AF37]/40 flex items-center justify-center mb-4"
        aria-hidden
      >
        <span className="font-serif text-[#D4AF37] text-2xl leading-none font-black">D</span>
        <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-[#D4AF37] shadow-[0_0_8px_rgba(212,175,55,0.6)]" />
      </div>
      <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-4">
        Diplomexa · Sign-In
      </p>

      {!showFallback && hasSession ? (
        <>
          <div className="h-5 w-5 rounded-full border-2 border-[#D4AF37]/20 border-t-[#D4AF37] animate-spin mb-4" />
          <h1 className="text-xl font-extrabold tracking-tight">Signing you in…</h1>
          <p className="mt-2 text-sm text-slate-400 max-w-sm">
            Hang tight — we're handing you back to the Diplomexa app.
          </p>
        </>
      ) : (
        <>
          <h1 className="text-xl font-extrabold tracking-tight">Almost there</h1>
          <p className="mt-2 text-sm text-slate-400 max-w-sm">
            {hasSession
              ? "Tap the button below to return to the Diplomexa app and finish signing in."
              : "We couldn't find a session. Please tap 'Continue with Google' again from the app."}
          </p>
          <div className="mt-6 flex flex-col gap-2 w-full max-w-xs">
            {deepLink && (
              <a
                href={deepLink}
                data-testid="mobile-bridge-open-app"
                className="inline-block px-4 py-3 rounded-xl font-bold uppercase tracking-[0.16em] text-xs bg-[#D4AF37] text-[#0A192F]"
              >
                Open Diplomexa app
              </a>
            )}
            <a
              href="/login"
              data-testid="mobile-bridge-web-fallback"
              className="inline-block px-4 py-3 rounded-xl font-bold uppercase tracking-[0.16em] text-xs border border-white/15 text-white"
            >
              Or continue on the web
            </a>
          </div>
        </>
      )}
    </div>
  );
}
